import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Email as EmailIcon,
  Logout as LogoutIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";
import PlanSelector from "../settings/PlanSelector";

import { can, permissionsPending } from '../../utils/access';
import { getRoleLabel } from '../../utils/roles';
import { getSubscriptionState, hasValidSubscription } from '../../utils/subscription';

/** Chip props for one organization row; literal t() calls keep the keys grep-able. */
function subscriptionChipProps(state, t) {
  switch (state) {
    case 'active':
      return { label: t("Subscription active"), color: 'success', variant: 'filled' };
    case 'trial':
      return { label: t("Trial"), color: 'info', variant: 'filled' };
    case 'expired':
      return { label: t("Subscription expired"), color: 'error', variant: 'outlined' };
    default:
      return { label: t("No subscription"), color: 'default', variant: 'outlined' };
  }
}

/**
 * The way out for a user who belongs to several organizations. The gate replaces the whole app
 * shell, and with it the sidebar OrganizationSelector, so without this card the only exits were
 * logging out or buying a plan for the very organization the user is stuck in.
 *
 * Every row is clickable, expired ones included: an admin of another lapsed organization needs
 * to reach that organization's gate to renew it. The chip tells the state at a glance.
 */
function OtherOrganizationsCard({ t, organizations, onSwitch }) {
  if (organizations.length === 0) {
    return null;
  }
  return (
    <Card variant="outlined" sx={{ mb: 4 }} data-testid="subscription-gate-org-switch">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t("Switch organization")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("You also belong to other organizations. Select one to continue working there.")}
        </Typography>
        <List disablePadding>
          {organizations.map((org) => {
            const chip = subscriptionChipProps(getSubscriptionState(org), t);
            return (
              <ListItemButton
                key={org.organization_id}
                onClick={() => onSwitch(org)}
                data-testid={`subscription-gate-org-${org.organization_id}`}
                sx={{ borderRadius: 1, gap: 1, flexWrap: 'wrap' }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText
                  primary={org.organization_name}
                  secondary={getRoleLabel(org.role, t)}
                  sx={{ flex: '1 1 160px', minWidth: 0, my: 0 }}
                />
                {/* Wrapped under the text on phones (indent = icon column), right-aligned otherwise. */}
                <Chip size="small" {...chip} sx={{ ml: { xs: 5.5, sm: 'auto' } }} />
              </ListItemButton>
            );
          })}
        </List>
      </CardContent>
    </Card>
  );
}

/**
 * SubscriptionGate component that blocks access when organization has no valid subscription.
 * - For employees: Shows notification to contact admin
 * - For admin/foreman: Shows subscription plans with buy option
 * - For anyone in several organizations: offers to switch to another one
 */
function SubscriptionGate({ t, children }) {
  const { user, currentOrganization, switchOrganization } = useUserStore();
  const [plans, setPlans] = useState([]);
  const [meteredPrices, setMeteredPrices] = useState(null);
  const [smsBilledPerSegment, setSmsBilledPerSegment] = useState(false);
  const [loading, setLoading] = useState(true);

  // Staff users bypass subscription check entirely
  const isStaff = user?.is_staff || user?.is_superuser;

  const subscriptionStatus = currentOrganization?.subscription_status;
  // While the payload has no permissions yet (deploy window) do not downgrade the panel.
  const canManageSubscription = permissionsPending(currentOrganization) || can(currentOrganization, "billing.manage_subscription");

  // Check if subscription is valid (active paid or active trial)
  const subscriptionValid = isStaff || hasValidSubscription(currentOrganization);

  // The user's other organizations, the ones they can work in listed first (sort is stable).
  const otherOrganizations = (user?.organizations || [])
    .filter((org) => org.organization_id !== currentOrganization?.organization_id)
    .sort((a, b) => Number(hasValidSubscription(b)) - Number(hasValidSubscription(a)));

  useEffect(() => {
    // Only fetch plans if user can manage subscription and doesn't have a valid one
    if (canManageSubscription && !subscriptionValid) {
      fetchPlans();
    } else {
      setLoading(false);
    }
  }, [canManageSubscription, subscriptionValid]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get("/api/payments/plans/");
      setPlans(response.data?.plans || []);
      setMeteredPrices(response.data?.metered_prices || null);
      setSmsBilledPerSegment(Boolean(response.data?.sms_billed_per_segment));
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    } finally {
      setLoading(false);
    }
  };

  // If subscription is valid, render children
  if (subscriptionValid) {
    return children;
  }

  // Show loading while checking
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // No valid subscription - show appropriate view
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <WarningIcon sx={{ fontSize: 64, color: "warning.main", mb: 2 }} />
        <Typography variant="h4" gutterBottom fontWeight={600}>
          {t("Subscription Required")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {subscriptionStatus?.has_subscription
            ? t("Your organization's subscription has expired.")
            : t("Your organization does not have an active subscription.")}
        </Typography>
      </Box>

      {/* Fastest exit first: PlanSelector is tall on mobile and the employee card is a dead end. */}
      <OtherOrganizationsCard t={t} organizations={otherOrganizations} onSwitch={switchOrganization} />

      {canManageSubscription ? (
        // Admin/Foreman view - show plans
        <Box>
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="body2">
              {t("As an administrator, you can subscribe to a plan below to continue using Verstaani.")}
            </Typography>
          </Alert>

          {plans.length > 0 ? (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  {t("Choose Your Plan")}
                </Typography>
                <PlanSelector
                  plans={plans}
                  meteredPrices={meteredPrices}
                  smsBilledPerSegment={smsBilledPerSegment}
                  currentPlan={null}
                  isTrialActive={false}
                  onCheckoutStarted={() => {}}
                />
              </CardContent>
            </Card>
          ) : (
            <Alert severity="warning">
              {t("No subscription plans available. Please contact support.")}
            </Alert>
          )}
        </Box>
      ) : (
        // Employee view - ask to contact admin
        <Card variant="outlined">
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <EmailIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t("Contact Your Administrator")}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t("Please contact an administrator of your organization to activate or renew the subscription.")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("Once the subscription is active, you'll be able to access all features.")}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Help section */}
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {t("Need help?")}{" "}
          <Button
            variant="text"
            size="small"
            href="mailto:asiakaspalvelu@verstaani.fi"
          >
            asiakaspalvelu@verstaani.fi
          </Button>
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<LogoutIcon />}
          onClick={() => { window.location.href = '/accounts/logout/'; }}
          sx={{ mt: 2 }}
        >
          {t("Log out")}
        </Button>
      </Box>
    </Container>
  );
}

export default withTranslation()(SubscriptionGate);
